import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { RadioButton } from "primereact/radiobutton";
import styled from "styled-components";
import { SupClass } from "../api/models/supClass.models";
import {
  BasicProductInfo,
  DialogInputs,
  useMaterials,
} from "../hooks/useMaterials";
import { v4 as uuidv4 } from "uuid";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { Family } from "../api/models/fullProductData.models";
import {
  CreatePackageRowPayload,
  PackageRow,
  UpdatePackageRowPayload,
} from "../api/models/packageRow.models";
import {
  CreateProductPayload,
  Product,
  UpdateProductPayload,
} from "../api/models/product.models";
import AddMaterialInputs from "../components/addMaterialInputs";
import SupSelection from "../components/supSelection";
import {
  convertAkeneoRowsToEcomodRows,
  convertEcomodRowsToAkeneoRows,
  getDefaultValuesFromPackageValues,
  getTotalPrice,
} from "../utils/rowUtils";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import usePackageRow from "../hooks/usePackageRow";
import useProducts from "../hooks/useProducts";
import { Package } from "../api/models/package.models";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { SearchProduct } from "../api/models/searchProduct.models";
import { ReportRowType } from "../hooks/useReportRows";
import { Price } from "../api/models/prices.models";
import { InfoDot } from "../components/infoDot";
import useSynkkaEanSearch from "../hooks/useSynkkaEanSearch";
import BasicProductInfoFields from "../components/basicProductInfoFields";
import PriceYearSelector, {
  PriceYearNote,
} from "../components/priceYearSelector";
import { confirmDialog } from "primereact/confirmdialog";
import {
  RowValues,
  RowValuesWithEcomodClass,
} from "../api/models/rowValues.models";

const SectionHeader = styled.h4`
  font-weight: 500;
  font-size: 20px;
  letter-spacing: -0.4px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  gap: 0.25rem;

  & .p-inputtext,
  & .p-inputnumber {
    width: 100%;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  & > div {
    flex: 1;
    min-width: 200px;
  }

  & .p-radiobutton {
    margin-bottom: 2px;
  }
`;

const StickyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  width: 100%;
  bottom: 0;
  background-color: #fff;
  z-index: 100;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
`;

const Section = styled.section`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

interface CreateAkeneoProductFormProps {
  supClasses?: SupClass[];
  ecomodClasses?: EcomodClass[];
  selectedSearchProduct?: SearchProduct | null;
  onHide: () => void;
  selectMaterialSelection: "materials" | "package";
  setSelectMaterialSelection: React.Dispatch<
    React.SetStateAction<"materials" | "package">
  >;
  packages: Package[];
  selectedPackage?: Package | null;
  setSelectedPackage: React.Dispatch<React.SetStateAction<Package | null>>;
  isLoadingPackages: boolean;
  productData?: Product | null;
  handleCreateNewReportRow?: (
    data: DialogInputs,
    reportRowType: ReportRowType,
  ) => void;
  presetEAN?: string;
  presetName?: string;
  preview?: boolean;
  refetchSearchProducts?: () => void;
  synkkaPackageRows?: PackageRow | undefined;
  initialBasicInfo?: BasicProductInfo;
  onBasicInfoChange?: (values: BasicProductInfo) => void;
  pricesData?: Price[];
  useSynkkaSearch?: boolean;
  priceYearOverride?: number | null;
  setPriceYearOverride?: (year: number | null) => void;
  priceYearOptions?: { label: string; value: number }[];
  priceDefaultYear?: number;
  isLoadingPrices?: boolean;
}

const CreateAkeneoProductForm: React.FC<CreateAkeneoProductFormProps> = ({
  supClasses,
  ecomodClasses,
  selectedSearchProduct,
  onHide,
  selectMaterialSelection,
  setSelectMaterialSelection,
  packages,
  selectedPackage,
  setSelectedPackage,
  isLoadingPackages,
  productData,
  handleCreateNewReportRow,
  presetEAN,
  presetName,
  preview = false,
  refetchSearchProducts,
  synkkaPackageRows,
  initialBasicInfo,
  onBasicInfoChange,
  pricesData,
  useSynkkaSearch = false,
  priceYearOverride,
  setPriceYearOverride,
  priceYearOptions = [],
  priceDefaultYear,
  isLoadingPrices = false,
}) => {
  const [companyId] = useUIStore(
    useShallow((state: UIState) => [state.companyId]),
  );
  const { t } = useTranslation();
  const pathname = window.location.pathname;

  const [synkkaSearchEan, setSynkkaSearchEan] = useState<string>("");
  const [synkkaProductImported, setSynkkaProductImported] =
    useState<boolean>(false);
  const [synkkaImporterProductValues, setSynkkaImporterProductValues] =
    useState<RowValues[]>([]);
  const [isSup, setIsSup] = useState(false);
  const [supClassCode, setSupClassCode] = useState<string | undefined>(
    undefined,
  );

  const sortedPackages = useMemo(
    () => [...packages].sort((a, b) => a.name.localeCompare(b.name)),
    [packages],
  );

  const [filteredPackages, setFilteredPackages] =
    useState<Package[]>(sortedPackages);

  const { multiMaterialPackageCodes } = useMaterials();

  useEffect(() => {
    setFilteredPackages(sortedPackages);
  }, [sortedPackages]);

  const searchPackages = (event: AutoCompleteCompleteEvent) => {
    const query = event.query.trim().toLowerCase();
    const results = query
      ? sortedPackages.filter((pkg) => pkg.name.toLowerCase().includes(query))
      : sortedPackages;
    // always create a new array reference so the panel opens even when results are unchanged
    setFilteredPackages([...results]);
  };

  const {
    packageRow,
    isLoadingPackageRow,
    createNewPackageRow,
    isCreatingPackageRow,
    updateExistingPackageRow,
    isUpdatingPackageRow,
  } = usePackageRow({
    packageRowIdentifier: productData?.packageRowsAssociation || null,
  });

  const { packageRow: selectedPackageRow } = usePackageRow({
    packageRowIdentifier: selectedPackage?.packageRowsAssociation || null,
  });

  const {
    createNewAkeneoProduct,
    isCreatingProduct,
    updateExistingProduct,
    isUpdatingProduct,
  } = useProducts({
    companyId: companyId as number,
  });

  const containsOnlyNumbers = (str?: string) => {
    if (!str) return false;
    return /^\d*$/.test(str);
  };

  const materialDefaultValues = useMemo(() => {
    return getDefaultValuesFromPackageValues(
      packageRow?.rowValues ||
        synkkaPackageRows?.rowValues ||
        synkkaImporterProductValues ||
        [],
      ecomodClasses || [],
      supClasses || [],
    );
  }, [
    packageRow,
    synkkaPackageRows,
    synkkaImporterProductValues,
    ecomodClasses,
    supClasses,
  ]);

  useEffect(() => {
    if (materialDefaultValues.length > 0) {
      setIsSup(!!materialDefaultValues[0].isSup);
      setSupClassCode(materialDefaultValues[0].supClass);
    }
  }, [materialDefaultValues]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
    watch,
    trigger,
  } = useForm<DialogInputs>({
    defaultValues: {
      name: initialBasicInfo?.name ?? (productData?.name || presetName || ""),
      ean:
        initialBasicInfo?.ean ??
        (productData?.ean ||
          (presetEAN && containsOnlyNumbers(presetEAN) ? presetEAN : "")),
      productNumber:
        initialBasicInfo?.productNumber ??
        (productData?.internalReference ||
          (presetEAN && !containsOnlyNumbers(presetEAN) ? presetEAN : "")),
      responsibleToReport:
        initialBasicInfo?.responsibleToReport ??
        (productData?.responsibleToReport !== undefined
          ? productData.responsibleToReport
          : true),
      comment: initialBasicInfo?.comment ?? (productData?.comment || ""),
    },
    mode: "onBlur",
  });

  const handleBlur = async () => {
    await trigger(); // Validates all fields
  };

  const handleSynkkaSearch = async (ean: string) => {
    setSynkkaSearchEan(ean);
  };

  const { synkkaProducts, fetchFullProduct, isLoadingFullProduct } =
    useSynkkaEanSearch({
      ean: synkkaSearchEan,
      enabled: useSynkkaSearch && synkkaSearchEan.trim() !== "",
    });

  useEffect(() => {
    if (
      useSynkkaSearch &&
      synkkaProducts.length > 0 &&
      !synkkaProductImported
    ) {
      const synkkaProduct = synkkaProducts[0];
      if (
        synkkaProduct.name !== getValues("name") &&
        synkkaProduct.family === Family.product
      ) {
        confirmDialog({
          header: t("text-synkka-product-import"),
          message: t("header-import-synkka-product", { ean: synkkaSearchEan }),
          icon: "pi pi-exclamation-triangle",
          acceptLabel: t("cmd-yes"),
          rejectLabel: t("cmd-no"),
          accept: async () => {
            setSynkkaProductImported(true);

            // Fetch full product
            const fullProductResult = await fetchFullProduct([
              synkkaProduct.ean as string,
            ]);

            // Take first result, should always be there
            const fullProductData = fullProductResult[0];
            reset({
              name: fullProductData.product.name,
              ean: fullProductData.product.ean,
              productNumber: fullProductData.product.internalReference || "",
              responsibleToReport: true,
            });

            setSynkkaImporterProductValues(
              fullProductData.packageRows?.rowValues,
            );
            trigger();
          },
        });
      }
    }
  }, [synkkaProducts, getValues, t, useSynkkaSearch]);

  // Change the selection based on existing associations in edit product view
  React.useEffect(() => {
    if (productData?.packageAssociation) {
      setSelectMaterialSelection("package");
      const associatedPackage = packages.find(
        (pkg) => pkg.identifier === productData.packageAssociation,
      );
      setSelectedPackage(associatedPackage || null);
    }

    if (productData?.packageRowsAssociation) {
      setSelectMaterialSelection("materials");
    }
  }, [productData]);

  const handleCreateNew = async (data: DialogInputs) => {
    const packageRowIdentifier = uuidv4();

    // if user has selected materials, create package row first,
    // else just create product and attach existing package identifier as packageAssociation
    if (selectMaterialSelection === "materials") {
      const packageRowPayload: CreatePackageRowPayload = {
        identifier: packageRowIdentifier,
        companyId: (companyId as number).toString(),
        name: data.name,
        family: Family.package_rows,
        synkkaImported: false,
        rowValues: convertEcomodRowsToAkeneoRows(data.values),
      };

      await createNewPackageRow(packageRowPayload);
    }

    const payload: CreateProductPayload = {
      identifier: uuidv4(),
      name: data.name,
      companyId: (companyId as number).toString(),
      family: Family.product,
      ean: data.ean,
      packageRowsAssociation:
        selectMaterialSelection === "materials" ? packageRowIdentifier : "null",
      responsibleToReport: data.responsibleToReport,
      comment: data.comment,
      packageAssociation:
        selectMaterialSelection === "package"
          ? selectedPackage?.identifier
          : "null",
      internalReference: data.productNumber || "",
    };

    const result = await createNewAkeneoProduct(payload);

    refetchSearchProducts && refetchSearchProducts();

    if (!!result) {
      // Reset form and close dialog
      setValue("values", []);
      onHide();
    }
  };

  const handleUpdateExisting = async (data: DialogInputs) => {
    if (!selectedSearchProduct) return;

    let packageRowsIdentifier = packageRow?.identifier || null;

    if (selectMaterialSelection === "materials" && packageRow) {
      // Update existing package row:
      const packageRowPayload: UpdatePackageRowPayload = {
        identifier: packageRow?.identifier as string,
        companyId: (companyId as number).toString(),
        name: data.name,
        family: Family.package_rows,
        synkkaImported: false,
        rowValues: convertEcomodRowsToAkeneoRows(data.values),
      };
      await updateExistingPackageRow(packageRowPayload);
    }

    if (selectMaterialSelection === "materials" && !packageRow) {
      // If user has changed from materials to package there isnt a package row , create new one
      packageRowsIdentifier = uuidv4();
      const packageRowPayload: CreatePackageRowPayload = {
        identifier: packageRowsIdentifier,
        companyId: (companyId as number).toString(),
        name: data.name,
        family: Family.package_rows,
        synkkaImported: false,
        rowValues: [],
      };
      await createNewPackageRow(packageRowPayload);
    }

    const productPayload: UpdateProductPayload = {
      identifier: selectedSearchProduct.identifier,
      name: data.name,
      ean: data.ean,
      family: Family.product,
      packageRowsAssociation:
        selectMaterialSelection === "materials"
          ? packageRowsIdentifier || undefined
          : "null",
      companyId: (companyId as number).toString(),
      packageAssociation:
        selectMaterialSelection === "package"
          ? selectedPackage?.identifier
          : "null",
      responsibleToReport: data.responsibleToReport,
      comment: data.comment,
      internalReference: data.productNumber || "",
    };

    const result = await updateExistingProduct(productPayload);

    refetchSearchProducts && refetchSearchProducts();

    if (!!result) {
      // Reset form and close dialog
      setValue("values", []);
      onHide();
    }
  };

  const onSubmit = async (data: DialogInputs) => {
    if (selectedSearchProduct) {
      await handleUpdateExisting(data);
    } else if (handleCreateNewReportRow) {
      // If data was found from synkka, create new product first for company. Then create report row
      //isSynkkaData && (await handleCreateNew(data));
      await handleCreateNewReportRow(data, ReportRowType.ECOMOD);
    } else {
      await handleCreateNew(data);
    }
  };

  const saveDisabled = () => {
    if (selectMaterialSelection === "materials") {
      const rowValues = getValues("values");
      return (
        rowValues?.length === 0 ||
        rowValues?.some((value) => !value.ecoModulationClass) ||
        rowValues?.some((value) => value.businessUse === undefined) ||
        rowValues?.some((value) => value.value === 0) ||
        rowValues?.some(
          (value) =>
            value.ecoModulationClass &&
            multiMaterialPackageCodes.includes(
              value.ecoModulationClass.code || "",
            ) &&
            (!value.secMaterial || !value.secValueType || !value.secValue),
        ) ||
        !getValues("name") ||
        rowValues?.some(
          (value) =>
            !!value.secMaterial &&
            value.secValueType !== 2 &&
            (value.secValue ?? 0) > (value.value ?? 0),
        )
      );
    } else {
      return (
        selectMaterialSelection === "package" &&
        (!selectedPackage || !getValues("name"))
      );
    }
  };

  const watchName = watch("name");
  const watchResponsibleToReport = watch("responsibleToReport");
  const watchProductNumber = watch("productNumber");
  const watchEan = watch("ean");
  const watchComment = watch("comment");
  const watchValues = watch("values");

  useEffect(() => {
    onBasicInfoChange?.({
      name: watchName,
      ean: watchEan,
      productNumber: watchProductNumber,
      responsibleToReport: watchResponsibleToReport,
      comment: watchComment,
    });
  }, [
    watchName,
    watchEan,
    watchProductNumber,
    watchResponsibleToReport,
    watchComment,
    onBasicInfoChange,
  ]);

  const totalPrice = pricesData
    ? selectMaterialSelection === "package"
      ? selectedPackageRow
        ? getTotalPrice(
            0,
            pricesData,
            convertAkeneoRowsToEcomodRows(
              selectedPackageRow.rowValues,
              ecomodClasses || [],
              supClasses || [],
            ),
          )
        : null
      : getTotalPrice(0, pricesData, watchValues)
    : null;

  const roundedPrice =
    totalPrice !== null
      ? parseFloat(totalPrice.toFixed(4)).toString() + " €"
      : "-";

  return (
    <LoadingContainer
      status={
        isLoadingPackageRow || isLoadingFullProduct
          ? HttpRequestStatus.LOADING
          : HttpRequestStatus.SUCCESS
      }
    >
      <form className="product-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="form-container">
          <Section>
            <BasicProductInfoFields
              control={control}
              errors={errors}
              preview={preview}
              watchEan={watchEan}
              watchProductNumber={watchProductNumber}
              watchResponsibleToReport={watchResponsibleToReport}
              onEanBlur={() => {
                // On ean blur, also check the product number from synkka if ean matches with a product in synkka
                if (useSynkkaSearch && watchEan) {
                  handleSynkkaSearch(watchEan);
                }
              }}
              onFieldBlur={handleBlur}
              onResponsibleToReportUnchecked={() => setValue("comment", "")}
            />
          </Section>

          <Row>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                padding: "1rem 2.5rem 2rem 2rem",
              }}
            >
              <div>
                <RadioButton
                  inputId="material-selection-materials"
                  name="material-selection"
                  value="materials"
                  checked={selectMaterialSelection === "materials"}
                  onChange={() => setSelectMaterialSelection("materials")}
                  disabled={preview}
                />
                <InfoDot
                  position="right"
                  text={t("infotext-materials")}
                  marginLeft="10px"
                >
                  <label
                    htmlFor="material-selection-materials"
                    style={{ marginLeft: "0.5rem" }}
                  >
                    {t("label-materials")}
                  </label>
                </InfoDot>
              </div>
              <div>
                <RadioButton
                  inputId="material-selection-package"
                  name="material-selection"
                  value="package"
                  checked={selectMaterialSelection === "package"}
                  onChange={() => {
                    setSelectMaterialSelection("package");
                    setIsSup(false);
                    setSupClassCode(undefined);
                  }}
                  disabled={preview}
                />

                <InfoDot
                  position="right"
                  text={
                    pathname === "/models"
                      ? t("infotext-multipliable-package")
                      : t("infotext-multipliable-package-reporting")
                  }
                  marginLeft="10px"
                >
                  <label
                    htmlFor="material-selection-package"
                    style={{ marginLeft: "0.5rem" }}
                  >
                    {t("label-multipliable-package")}
                  </label>
                </InfoDot>
              </div>
            </div>
          </Row>

          <div style={{ paddingLeft: "2rem", paddingRight: "2.5rem" }}>
            {selectMaterialSelection === "materials" && (
              <SupSelection
                isSup={isSup}
                onIsSupChange={setIsSup}
                supClass={supClassCode}
                onSupClassChange={setSupClassCode}
                supClasses={supClasses}
                disabled={preview}
              />
            )}
          </div>

          {selectMaterialSelection === "package" && (
            <Section>
              <SectionHeader className="package-header">
                {preview
                  ? t("header-package")
                  : t("header-package-information")}
              </SectionHeader>

              <Row>
                <InputContainer>
                  <AutoComplete
                    id="package-dropdown"
                    value={selectedPackage}
                    suggestions={filteredPackages}
                    completeMethod={searchPackages}
                    field="name"
                    dropdown
                    forceSelection
                    placeholder={t("label-select-package")}
                    onChange={(e) => setSelectedPackage(e.value)}
                    style={{ width: "100%" }}
                    disabled={isLoadingPackages || preview}
                  />
                </InputContainer>
              </Row>
            </Section>
          )}

          {selectMaterialSelection === "materials" && (
            <Section>
              <SectionHeader className="material-header">
                {preview ? t("header-materials") : t("label-add-material")}
              </SectionHeader>

              <AddMaterialInputs
                setReportRowValues={(values) => {
                  setValue("values", values);
                  if (values?.length > 0) {
                    handleBlur();
                  }
                }}
                ecomodClasses={ecomodClasses}
                defaultValues={getDefaultValuesFromPackageValues(
                  packageRow?.rowValues ||
                    synkkaPackageRows?.rowValues ||
                    synkkaImporterProductValues ||
                    [],
                  ecomodClasses || [],
                  supClasses || [],
                )}
                supClasses={supClasses}
                preview={preview}
                isSup={isSup}
                supClass={supClassCode}
              />
            </Section>
          )}

          <Section>
            <SectionHeader>{t("header-combined-prices")}</SectionHeader>
            <LoadingContainer
              status={
                isLoadingPrices
                  ? HttpRequestStatus.LOADING
                  : HttpRequestStatus.SUCCESS
              }
            >
              <div>
                {!!roundedPrice &&
                  (selectMaterialSelection === "package"
                    ? !!selectedPackage
                    : watchValues?.length > 0) && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>
                        {t("label-combined-price-per-product-unit")}:{" "}
                        {roundedPrice} {t("label-vat-amount")}
                        {priceYearOverride && (
                          <PriceYearNote>
                            {`(${priceYearOverride} ${t("label-prices-from-year")})`}
                          </PriceYearNote>
                        )}
                      </span>
                      {setPriceYearOverride && (
                        <PriceYearSelector
                          priceYearOverride={priceYearOverride ?? null}
                          setPriceYearOverride={setPriceYearOverride}
                          priceYearOptions={priceYearOptions}
                          defaultYear={
                            priceDefaultYear ?? new Date().getFullYear()
                          }
                        />
                      )}
                    </div>
                  )}
              </div>
            </LoadingContainer>
          </Section>
        </div>

        <StickyActions>
          <Button
            role="cancel"
            type="button"
            label={t("cmd-cancel")}
            className="p-button-text"
            onClick={onHide}
            disabled={
              isCreatingProduct ||
              isCreatingPackageRow ||
              isUpdatingProduct ||
              isUpdatingPackageRow
            }
          />
          {!preview && (
            <Button
              style={{ marginRight: "4rem" }}
              type="submit"
              label={t("cmd-save")}
              className="p-ml-2"
              disabled={saveDisabled()}
              loading={
                isCreatingProduct ||
                isCreatingPackageRow ||
                isUpdatingProduct ||
                isUpdatingPackageRow
              }
            />
          )}
        </StickyActions>
      </form>
    </LoadingContainer>
  );
};

export default CreateAkeneoProductForm;
